import {doc, serverTimestamp, updateDoc} from 'firebase/firestore';
import {firestore} from '@/firebase/firebaseConfig';
import {getUserProfile} from '@/lib/UserProfile';
import {getSignupContext, type SignupContext} from '@/lib/signupContext';
import {getLocalStorageItem} from '@/lib/storageUtils';
import {
    USER_LOCALE_STORAGE_KEY,
    normalizeMarketingLocale,
} from '@/i18n/resolveLocale';
import type {Locale} from '@/i18n/locales';

export interface SignupAttribution {
    ref_page?: string;
    ft_source?: string;
    ft_medium?: string;
    ft_campaign?: string;
    ft_landing?: string;
}

function buildAttributionFromContext(ctx: SignupContext | null): SignupAttribution | null {
    if (!ctx) return null;

    const attribution: SignupAttribution = {};
    const keys: (keyof SignupAttribution)[] = [
        'ref_page',
        'ft_source',
        'ft_medium',
        'ft_campaign',
        'ft_landing',
    ];

    for (const key of keys) {
        const value = ctx[key]?.trim();
        if (value) attribution[key] = value;
    }

    return Object.keys(attribution).length > 0 ? attribution : null;
}

function resolveSignupLocale(): Locale | null {
    const ctx = getSignupContext();
    const fromContext = normalizeMarketingLocale(ctx?.locale);
    if (fromContext) return fromContext;

    return normalizeMarketingLocale(getLocalStorageItem(USER_LOCALE_STORAGE_KEY));
}

/**
 * Writes marketing attribution and locale to UserProfiles/{uid} after signup.
 * Idempotent: skips fields already present on the profile.
 */
export async function persistSignupProfileMetadata(uid: string): Promise<void> {
    if (!uid) return;

    try {
        const profile = await getUserProfile(uid);
        if (!profile) return;

        const locale = resolveSignupLocale();
        const attribution = buildAttributionFromContext(getSignupContext());
        const existingAttribution = profile.attribution;

        const preferredLocale = locale && !profile.preferredLocale ? locale : undefined;
        const attributionPatch = attribution && !existingAttribution ? attribution : undefined;

        if (!preferredLocale && !attributionPatch) return;

        await updateDoc(doc(firestore, 'UserProfiles', uid), {
            lastUpdated: serverTimestamp(),
            ...(preferredLocale ? {preferredLocale} : {}),
            ...(attributionPatch ? {attribution: attributionPatch} : {}),
        });
    } catch (error) {
        console.error('Failed to persist signup profile metadata:', error);
    }
}
